import { useCallback, useMemo } from 'react';

import { useAbsenceDaysCalculation } from '~/data/absences';
import { PublicHolidayDto } from '~/services/Http/client.people.generated';
import { isDayInDates, isSameDay } from '~/utils/helper';

type PropsType = {
  employeeId: number;
  publicHolidays: PublicHolidayDto[];
  absenceReasonCode?: string;
};

type UseAbsenceDateRangeOutput = {
  isPublicHoliday: (date: Date) => boolean;
  isUncountedWeekDay: (date: Date) => boolean;
  getPublicHolidayName: (date: Date) => string | undefined;
};
export const useAbsenceDateRange = (
  props: PropsType,
): UseAbsenceDateRangeOutput => {
  const { employeeId, publicHolidays, absenceReasonCode } = props;

  const { getUncountedWeekDays } = useAbsenceDaysCalculation({
    employeeId,
  });

  // Uncounted Week Days
  const uncountedWeekDays = useMemo(() => {
    return getUncountedWeekDays({ absenceReasonCode });
  }, [getUncountedWeekDays, absenceReasonCode]);

  const isUncountedWeekDay = useCallback(
    (date: Date) => {
      return uncountedWeekDays.includes(new Date(date).getDay());
    },
    [uncountedWeekDays],
  );

  // Public holidays
  const publicHolidayDates = useMemo(() => {
    return publicHolidays.map((ph) => new Date(ph.date));
  }, [publicHolidays]);

  const isPublicHoliday = useCallback(
    (date: Date) => {
      return isDayInDates(publicHolidayDates, date);
    },
    [publicHolidayDates],
  );

  const getPublicHolidayName = (date: Date) =>
    (isPublicHoliday(date) &&
      publicHolidays.find((ph) => isSameDay(ph.date, date))?.name) ||
    undefined;

  return {
    isPublicHoliday,
    isUncountedWeekDay,
    getPublicHolidayName,
  };
};
